import type { NpsConfig } from '@features/partners/builder/settings/nps/models/nps';
import type { NpsForm } from '@features/partners/builder/settings/nps/models/nps-form';
import type { Observable } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { NpsWebservice } from '@features/partners/builder/settings/nps/webservices/nps.webservice';
import { Store } from '@ngrx/store';
import { partnerId } from '@stores/partner/partner-revision/partner-revision.selectors';
import { filter, switchMap, take } from 'rxjs';

@Injectable()
export class NpsService {
  readonly #partnerId$ = inject(Store).select(partnerId);
  readonly partnerId = toSignal(this.#partnerId$, { initialValue: null });
  readonly #npsWebservice = inject(NpsWebservice);
  readonly #fb = inject(NonNullableFormBuilder);

  public readonly npsConfig$ = this.#partnerId$.pipe(
    filter(Boolean),
    take(1),
    switchMap((partnerId) => this.#npsWebservice.get(partnerId))
  );

  public readonly buildForm = (): NpsForm =>
    this.#fb.group({
      wizbiiNPS: [false],
      contentSquare: this.#fb.group({
        id: [{ value: '', disabled: true }, Validators.required],
        activated: [false],
      }),
      mopinion: this.#fb.group({
        id: [{ value: '', disabled: true }, Validators.required],
        activated: [false],
      }),
    });

  public readonly patchInitialValues = ({ config, form }: { config: NpsConfig; form: NpsForm }): void => {
    const { isNpsEnabled, contentSquare, mopinion } = config;
    const { controls } = form;

    controls.wizbiiNPS.patchValue(isNpsEnabled);

    if (contentSquare) {
      const { activated, id } = controls.contentSquare.controls;
      activated.patchValue(true);
      id.patchValue(contentSquare);
    }

    if (mopinion) {
      const { activated, id } = controls.mopinion.controls;
      activated.patchValue(true);
      id.patchValue(mopinion);
    }
  };

  public readonly onWizbiiNpsValueChanges = ({
    value,
    form: { controls },
  }: {
    value: boolean | undefined;
    form: NpsForm;
  }): void => {
    if (!value) {
      return;
    }

    controls.contentSquare.controls.activated.patchValue(false);
    controls.mopinion.controls.activated.patchValue(false);
  };

  public readonly onContentSquareActivatedValueChanges = ({
    value,
    form: { controls },
  }: {
    value: boolean | undefined;
    form: NpsForm;
  }): void => {
    if (!value) {
      controls.contentSquare.controls.id.disable();
      return;
    }

    controls.contentSquare.controls.id.enable();

    controls.wizbiiNPS.patchValue(false);
    controls.mopinion.controls.activated.patchValue(false);
  };

  public readonly onMopinionActivateValueChanges = ({
    value,
    form: { controls },
  }: {
    value: boolean | undefined;
    form: NpsForm;
  }): void => {
    if (!value) {
      controls.mopinion.controls.id.disable();
      return;
    }

    controls.mopinion.controls.id.enable();
    controls.wizbiiNPS.patchValue(false);
    controls.contentSquare.controls.activated.patchValue(false);
  };

  public updateNpsConfig(form: NpsForm): Observable<NpsConfig> {
    const { wizbiiNPS, contentSquare, mopinion } = form.getRawValue();

    const payload: NpsConfig = {
      isNpsEnabled: wizbiiNPS,
      contentSquare: contentSquare.activated ? contentSquare.id : null,
      mopinion: mopinion.activated ? mopinion.id : null,
    };

    return this.#partnerId$.pipe(
      filter(Boolean),
      take(1),
      switchMap((partnerId) => this.#npsWebservice.put({ partnerId, payload }))
    );
  }
}
